import { RELAY_STALE_MS } from "@domain/intent-relays-status";
import type { TaskItem } from "@domain/task-state";
import { taskRelayUnconfirmed } from "@domain/user-errors";

import { ClawbertMark } from "@/components/home/ClawbertMark";
import type { PendingCapture, TaskWriteKind } from "@/hooks/useTaskWrites";

import { Capture } from "./Capture";
import { HeldTaskCard } from "./HeldTaskCard";
import { TaskListRow } from "./TaskListRow";
import { relativeTime, TYPE_PRESENTATION } from "./typePresentation";

export interface TaskManagerViewProps {
  current: readonly TaskItem[];
  held: readonly TaskItem[];
  completed: readonly TaskItem[];
  isLoading: boolean;
  /** Navigate to a task's detail page. Threaded from the route layer. */
  onOpenTask?: (id: string) => void;
  /** Relay a complete intent for a row (praxis-intent-relay §3.7). */
  onCompleteTask?: (id: string) => void;
  /** Relay a decision answer for a held decision card. */
  onResolveDecision?: (id: string, answer: string) => void | Promise<void>;
  /** Relay a capture — rejects on refusal (draft survives). */
  onCapture?: (text: string) => Promise<void>;
  /** Tasks with a relayed write in flight (durable ∪ local). */
  pendingByTaskId?: ReadonlyMap<string, TaskWriteKind>;
  /** In-flight captures — ghost rows at the top of To do. */
  pendingCaptures?: readonly PendingCapture[];
  /** Inline relay error copy per task (failed / unconfirmed). */
  errorByTaskId?: ReadonlyMap<string, string>;
  /** Inline capture error copy. */
  captureError?: string | null;
}

const SECTION_LABEL =
  "mt-6 mb-2 font-body text-[11px] font-bold tracking-[0.14em] text-home-ink/40 dark:text-white/40 uppercase";

const EMPTY_PENDING: ReadonlyMap<string, TaskWriteKind> = new Map();
const EMPTY_ERRORS: ReadonlyMap<string, string> = new Map();

function summary(input: {
  done: number;
  working: number;
  held: number;
}): string {
  const parts: string[] = [];
  if (input.done > 0) parts.push(`${input.done} done`);
  if (input.working > 0) parts.push(`${input.working} working`);
  if (input.held > 0) parts.push(`${input.held} waiting on you`);
  return parts.length > 0 ? parts.join(" · ") : "Nothing on your plate";
}

/**
 * A ghost row for a capture whose bead hasn't projected yet. Keyed by
 * RelayId; replaced by the real row when the `relay:<RelayId>`-labeled
 * bead lands (the server computes `satisfied` and the relay row leaves
 * the pending set). Survives reload — signal A is durable.
 */
function GhostCaptureRow({ capture }: { capture: PendingCapture }) {
  // Render-time staleness (no timers, frontend-doctrine §8): a ghost
  // pending past the stale window swaps its caption for the unconfirmed
  // chip pointing at chat (FMEA 8 — e.g. the agent's worth-being-a-bead
  // filter declined the capture in chat).
  const stale = Date.now() - capture.requestedAt > RELAY_STALE_MS;
  // Perceived-instant capture: the ghost is styled as a regular task row
  // (solid card, type glyph, full-strength title, relative time) so the
  // task reads as "added" the moment it's submitted. Honest-pending
  // (§4.12) is preserved where it matters — the check stays the pulsing
  // sky in-flight glyph with `aria-busy`, never a state the projection
  // could contradict, and the row is still replaced by truth when the
  // `relay:<id>`-labeled bead lands. The next-action glyph is a
  // presentation default; the projected row brings the agent's real
  // inferred type.
  const { icon: Icon, tone } = TYPE_PRESENTATION["next-action"];
  return (
    <div
      aria-busy="true"
      className="flex items-center gap-3 rounded-2xl border border-home-ink/8 dark:border-white/8 bg-white dark:bg-surface-dark-card px-4 py-3.5 shadow-[0_10px_26px_-22px_rgba(33,31,28,0.4)]"
    >
      <span className="h-[22px] w-[22px] shrink-0 animate-pulse rounded-full border-2 border-home-sky ring-2 ring-home-sky/30 dark:ring-home-sky/40" />
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${tone}`}
      >
        <Icon className="h-4 w-4" strokeWidth={2.4} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-body text-[13.5px] font-semibold text-home-ink/85 dark:text-white/85">
          {capture.text}
        </p>
        {stale ? (
          <p className="truncate font-body text-[12px] text-home-terracotta">
            {taskRelayUnconfirmed()}
          </p>
        ) : null}
      </div>
      <span className="shrink-0 font-body text-[11px] text-home-ink/40 dark:text-white/40">
        {relativeTime(capture.requestedAt, Date.now())}
      </span>
    </div>
  );
}

/**
 * The live task-manager surface: capture at the top, a "waiting on you"
 * section, the current list (working + to-do, with ghost rows for
 * in-flight captures), and completed. Pure — props in, JSX out; queries
 * live in `useTasks`, write relays in `useTaskWrites`.
 */
export function TaskManagerView({
  current,
  held,
  completed,
  isLoading,
  onOpenTask,
  onCompleteTask,
  onResolveDecision,
  onCapture,
  pendingByTaskId = EMPTY_PENDING,
  pendingCaptures = [],
  errorByTaskId = EMPTY_ERRORS,
  captureError,
}: TaskManagerViewProps) {
  const open = (id: string) =>
    onOpenTask !== undefined ? () => onOpenTask(id) : undefined;
  const complete = (item: TaskItem) =>
    onCompleteTask !== undefined && item.completable
      ? () => onCompleteTask(item.id)
      : undefined;
  const rowProps = (item: TaskItem) => ({
    onOpen: open(item.id),
    onComplete: complete(item),
    pendingWrite: pendingByTaskId.has(item.id) || undefined,
    relayError: errorByTaskId.get(item.id),
  });
  const working = current.filter((i) => i.displayState === "working");
  const todo = current.filter((i) => i.displayState === "todo");
  const ghosts = pendingCaptures;

  return (
    <div className="mx-auto min-w-0 max-w-2xl">
      <div>
        <h2 className="font-display text-[26px] font-semibold tracking-tight text-home-ink dark:text-white">
          Tasks
        </h2>
        <p className="mt-1 font-body text-[14px] text-home-ink/55 dark:text-white/55">
          {isLoading
            ? "Loading…"
            : summary({
                done: completed.length,
                working: working.length,
                held: held.length,
              })}
        </p>
      </div>

      {onCapture !== undefined ? (
        <Capture
          onCapture={onCapture}
          // Only ACTIVE ghosts hold the input — a stale (unconfirmed)
          // capture shows its chip but must not lock the affordance
          // until the 24h sweep.
          pending={ghosts.some(
            (g) => Date.now() - g.requestedAt <= RELAY_STALE_MS,
          )}
          error={captureError}
        />
      ) : null}

      {isLoading ? (
        <p className="mt-10 text-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
          Loading your tasks…
        </p>
      ) : (
        <>
          {held.length > 0 ? (
            <>
              <p className={SECTION_LABEL}>Waiting on you</p>
              <div className="space-y-2">
                {held.map((item) => (
                  <HeldTaskCard
                    key={item.id}
                    item={item}
                    onOpen={open(item.id)}
                    {...(onResolveDecision !== undefined &&
                    item.heldReason === "decision"
                      ? {
                          onResolveDecision: (answer: string) =>
                            onResolveDecision(item.id, answer),
                          pending: pendingByTaskId.has(item.id),
                          error: errorByTaskId.get(item.id) ?? null,
                        }
                      : {})}
                  />
                ))}
              </div>
            </>
          ) : null}

          {working.length > 0 ? (
            <>
              <p className={SECTION_LABEL}>Working now</p>
              <div className="space-y-2">
                {working.map((item) => (
                  <TaskListRow key={item.id} item={item} {...rowProps(item)} />
                ))}
              </div>
            </>
          ) : null}

          {todo.length > 0 || ghosts.length > 0 ? (
            <>
              <p className={SECTION_LABEL}>To do</p>
              <div className="space-y-2">
                {ghosts.map((capture) => (
                  <GhostCaptureRow key={capture.relayId} capture={capture} />
                ))}
                {todo.map((item) => (
                  <TaskListRow key={item.id} item={item} {...rowProps(item)} />
                ))}
              </div>
            </>
          ) : null}

          {current.length === 0 && ghosts.length === 0 ? (
            <div className="mt-10 flex flex-col items-center gap-3 text-center">
              <ClawbertMark pose="sold" className="h-20 w-20" />
              <p className="font-body text-[14px] font-semibold text-home-ink/70 dark:text-white/70">
                Nothing in progress
              </p>
              <p className="font-body text-[13px] text-home-ink/45 dark:text-white/45">
                {onCapture !== undefined
                  ? "Capture something above, or ask in chat."
                  : "Ask in chat and new tasks will show up here."}
              </p>
            </div>
          ) : null}

          {completed.length > 0 ? (
            <>
              <p className={SECTION_LABEL}>Done</p>
              <div className="space-y-2">
                {completed.map((item) => (
                  <TaskListRow key={item.id} item={item} onOpen={open(item.id)} />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
